import React from 'react'
import { css } from '@emotion/core'
import {
  AncestorsLifeHistoryCard,
  FamilyCard,
  NameMeaningCard,
  SourcesCard,
  TimelineCard,
  PersonMasonryLayout,
} from '@fs/zion-tree-person-info-cards'
import ErrorBoundary from '@fs/zion-error-boundary'
import { usePersonAboutData } from '../../../../../hooks/usePersonAboutData'
import { PersonType } from '../../../../../propTypes/personDataProps'
import useDSApplicationMode from '../../../../../hooks/useDSApplicationMode'

const containerCss = css`
  padding: 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const loadingCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
`

const hideViewWithMapCss = css`
  /* Hide "View with map" button - multiple selector strategies.
     The shared zion TimelineCard hardcodes a link to the Tree app's
     /timeline/:personId route, which doesn't exist in Discovery, so the
     button is hidden in every mode (DSC-9135). */
  button[data-testid*='view-with-map'],
  a[data-testid*='view-with-map'],
  button[href*='/timeline/'] {
    display: none !important;
  }
`

const kioskTimelineCss = css`
  /* Disable timeline event interactivity (kiosk/STATION only) */
  [data-testid^='timeline-event-'],
  [data-testid*='timeline-event'],
  [id^='timeline-event-'] {
    pointer-events: none;
  }
`

export default function PersonAbout({ personData }) {
  const { personId, name, gender, lifespan } = personData
  const isWebMode = useDSApplicationMode() === 'WEB'

  const {
    person: aboutPerson,
    sources,
    families,
    lifeSketch,
    nameMeanings,
    timelineEvents,
    loading,
  } = usePersonAboutData(personId)

  const handlePersonClick = () => {
    // Prevent default click behavior on individuals
    return null
  }

  const person = {
    id: personId,
    name: aboutPerson?.name || name,
    gender: aboutPerson?.gender || gender,
    lifespan: aboutPerson?.lifespan || lifespan,
    fullLifespan: aboutPerson?.fullLifespan,
    birthPlace: aboutPerson?.birthPlace,
    deathPlace: aboutPerson?.deathPlace,
    portrait: aboutPerson?.portrait,
    ...personData,
  }

  const focusFamily = families.parents[0] || { parent1: null, parent2: null, children: [] }
  const focusSpouseFamily = families.spouses[0] || null

  if (loading) {
    return (
      <div css={containerCss}>
        <div css={loadingCss}>Loading...</div>
      </div>
    )
  }

  return (
    <div css={containerCss}>
      <ErrorBoundary>
        <PersonMasonryLayout>
          <AncestorsLifeHistoryCard person={person} text={lifeSketch} />

          {families.parents.length > 0 && (
            <FamilyCard
              families={families}
              focusFamily={focusFamily}
              type="parents"
              focusPersonId={personId}
              {...(!isWebMode && { customPersonLinkOrOverlay: handlePersonClick })}
            />
          )}

          {families.spouses.length > 0 && focusSpouseFamily && (
            <FamilyCard
              families={families}
              focusFamily={focusSpouseFamily}
              type="couple"
              focusPersonId={personId}
              {...(!isWebMode && { customPersonLinkOrOverlay: handlePersonClick })}
            />
          )}

          {timelineEvents.length > 0 && (
            <div css={[hideViewWithMapCss, !isWebMode && kioskTimelineCss]}>
              <TimelineCard ancestor={person} timelineEvents={timelineEvents} />
            </div>
          )}

          {sources.length > 0 && isWebMode && (
            <SourcesCard sources={sources} sourcesCount={aboutPerson?.sourcesCount || sources.length} person={person} />
          )}

          {nameMeanings.length > 0 && <NameMeaningCard meanings={nameMeanings} />}
        </PersonMasonryLayout>
      </ErrorBoundary>
    </div>
  )
}

PersonAbout.propTypes = {
  personData: PersonType,
}
